import { useApiUtils, useCustomFetch } from '~/composables';
import { omit as lo_omit } from 'es-toolkit';
import { pathJoin } from '~/utils';
import type {
    ComposableOptionsBase,
    CustomPagination,
    PaginatedPayload,
    PaginatedRequest,
    ApiQueryParams,
    CustomPaginationBase
} from '~/types';
import type { NitroFetchOptions } from 'nitropack';

export interface UseApiCommonOptions extends ComposableOptionsBase {
    apiBasePath: Nilish<string>;
}
export function useApiCommon(options: Partial<UseApiCommonOptions> = {}) {
    const apiBasePath = options.apiBasePath ?? `/v1`;
    const useApiUtilsObj = useApiUtils();
    const $_fetch = useCustomFetch({ siteContext: options.siteContext });

    async function getPagedListItems<T>(path: string, args?: PaginatedRequest): Promise<PaginatedPayload<T> | Error> {
        const url = pathJoin(apiBasePath, path);
        const config: NitroFetchOptions<string> = {
            method: 'GET',
            query: { page_size: 100, ...(args?.params || {}) }
        };

        let paginatedPayload: PaginatedPayload<T>;
        let payload: T[];

        try {
            const response = await $_fetch<CustomPagination<T>>(url, config);
            payload = response.results;

            paginatedPayload = {
                pagination: lo_omit(response, ['results']),
                data: payload
            };

            return paginatedPayload;
        } catch (err) {
            return useApiUtilsObj.getErrorObj(err);
        }
    }

    async function getItem<T>(path: string, args?: ApiQueryParams): Promise<T | Error> {
        const url = pathJoin(apiBasePath, path);
        const config: NitroFetchOptions<string> = {
            method: 'GET',
            query: args || {}
        };

        try {
            return await $_fetch<T>(url, config) as T;
        } catch (err) {
            return useApiUtilsObj.getErrorObj(err);
        }
    }

    function getSinglePagePaginationObj(count: number): CustomPaginationBase {
        return {
            all: false,
            count,
            pageSize: count,
            totalPages: 1,
            page: 1,
            next: '',
            previous: '',
            hasNext: false,
            hasPrevious: false
        };
    }

    return {
        getPagedListItems,
        getItem,
        getSinglePagePaginationObj
    };
}
